import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "./api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Vehicles from "./pages/Vehicles";
import VehicleDetail from "./pages/VehicleDetail";
import Purchases from "./pages/Purchases";
import Imports from "./pages/Imports";
import PortDouane from "./pages/PortDouane";
import Expenses from "./pages/Expenses";
import Maintenance from "./pages/Maintenance";
import Fuel from "./pages/Fuel";
import Tires from "./pages/Tires";
import Sales from "./pages/Sales";
import Reservations from "./pages/Reservations";
import Rentals from "./pages/Rentals";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import SupplierDebts from "./pages/SupplierDebts";
import Credits from "./pages/Credits";
import Encaissements from "./pages/Encaissements";
import Accounting from "./pages/Accounting";
import Treasury from "./pages/Treasury";
import Reports from "./pages/Reports";
import Rentabilite from "./pages/Rentabilite";
import Documents from "./pages/Documents";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    api
      .post("/auth/google/session", { session_id: sid })
      .then((r) => {
        localStorage.setItem("ag_token", r.data.access_token);
        setUser(r.data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true, state: { welcome: true } });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#05070B]">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-cyan-400 border-t-transparent" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/inscription" element={<Register />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="vehicules" element={<Vehicles />} />
        <Route path="vehicules/:id" element={<VehicleDetail />} />
        <Route path="achats" element={<Purchases />} />
        <Route path="dettes-fournisseurs" element={<SupplierDebts />} />
        <Route path="importations" element={<Imports />} />
        <Route path="port-douane" element={<PortDouane />} />
        <Route path="depenses" element={<Expenses />} />
        <Route path="entretiens" element={<Maintenance />} />
        <Route path="carburant" element={<Fuel />} />
        <Route path="pneus" element={<Tires />} />
        <Route path="ventes" element={<Sales />} />
        <Route path="reservations" element={<Reservations />} />
        <Route path="locations" element={<Rentals />} />
        <Route path="clients" element={<Customers />} />
        <Route path="fournisseurs" element={<Suppliers />} />
        <Route path="credits" element={<Credits />} />
        <Route path="encaissements" element={<Encaissements />} />
        <Route path="comptabilite" element={<Accounting />} />
        <Route path="caisse" element={<Treasury />} />
        <Route path="rapports" element={<Reports />} />
        <Route path="rentabilite" element={<Rentabilite />} />
        <Route path="documents" element={<Documents />} />
        <Route path="utilisateurs" element={<Users />} />
        <Route path="parametres" element={<Settings />} />
        <Route path="profil" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (localStorage.getItem("ag_theme") === "dark") {
      document.documentElement.classList.add("dark");
    }
    const onUnhandled = (e) => {
      if (e?.reason?.isAxiosError) e.preventDefault();
    };
    window.addEventListener("unhandledrejection", onUnhandled);
    return () => window.removeEventListener("unhandledrejection", onUnhandled);
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
